// src/pages/modals/ModalMovimientosCaja.js
//
// Modal para registrar ingresos o egresos de dinero en la caja
//  ▸ Endpoint:
//       POST /pos_cajas/movimientos
//       Body { tipo: 'ingreso'|'egreso', monto: number, descripcion: string }

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Typography,
  Stack,
  IconButton,
  InputAdornment,
  Divider,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  AddCircleOutline,
  RemoveCircleOutline,
  Close,
  AccountBalanceWallet,
  AttachMoney,
} from '@mui/icons-material';
import api from '../../api/api';

const QUICK_REASONS = {
  egreso: [
    'Retiro de caja',
    'Retiro para depósito',
    'Gasto menor',
    'Pago a proveedor',
    'Compra local',
    'Ajuste de caja',
  ],
  ingreso: [
    'Aporte a caja',
    'Vuelto inicial',
    'Ajuste de caja',
    'Ingreso manual',
  ],
};


function toNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function normalizeCajaResponse(data) {
  if (!data) return null;

  if (data?.caja && typeof data.caja === 'object') return data.caja;
  if (data?.data?.caja && typeof data.data.caja === 'object') return data.data.caja;
  if (data?.data && typeof data.data === 'object') return data.data;

  return data;
}

function isCajaAbiertaResponse(data) {
  return (
    data?.abierta === true ||
    data?.isAbierta === true ||
    data?.caja_abierta === true ||
    String(data?.estado || '').toUpperCase() === 'ABIERTA' ||
    String(data?.caja?.estado || '').toUpperCase() === 'ABIERTA'
  );
}

function getSaldoTeoricoEfectivo(cajaRaw) {
  const caja = normalizeCajaResponse(cajaRaw) || {};

  const saldoBackend =
    caja?.saldo_teorico_efectivo ??
    caja?.total_a_rendir_efectivo;

  if (saldoBackend !== undefined && saldoBackend !== null && saldoBackend !== '') {
    const n = Number(saldoBackend);
    return Number.isFinite(n) ? Math.max(0, n) : null;
  }

  const tieneFallback =
    caja?.monto_inicial !== undefined ||
    caja?.ventas_efectivo !== undefined ||
    caja?.mov_manual !== undefined;

  if (!tieneFallback) return null;

  return Math.max(
    0,
    toNumber(caja?.monto_inicial) +
      toNumber(caja?.ventas_efectivo) +
      toNumber(caja?.mov_manual)
  );
}

export default function ModalMovimientosCaja({ open, onClose, onSaved }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [tipo, setTipo] = useState('egreso');
  const [montoStr, setMontoStr] = useState('');
  const [descripcion, setDesc] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    msg: '',
    sev: 'success',
  });

  const [saldoEfectivo, setSaldoEfectivo] = useState(null);
  const [loadingSaldo, setLoadingSaldo] = useState(false);
  const [saldoError, setSaldoError] = useState('');

  const isEgreso = tipo === 'egreso';

  const fmtCLPInput = (value) => {
    const nums = String(value || '').replace(/\D/g, '');

    return nums
      ? new Intl.NumberFormat('es-CL').format(parseInt(nums, 10))
      : '';
  };

  const parseCLPInput = (value) =>
    parseInt(String(value || '').replace(/\./g, ''), 10) || 0;

  const monto = useMemo(() => parseCLPInput(montoStr), [montoStr]);

  const formattedMonto = useMemo(() => {
    if (!monto) return '$0';

    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(monto);
  }, [monto]);

  const descripcionLimpia = descripcion.trim();

  const formattedSaldo = useMemo(() => {
    if (saldoEfectivo === null) return '—';

    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(saldoEfectivo);
  }, [saldoEfectivo]);

  const excedeSaldo =
    isEgreso &&
    saldoEfectivo !== null &&
    monto > saldoEfectivo;

  const canSave =
    monto > 0 &&
    descripcionLimpia.length > 0 &&
    !isSaving &&
    (
      !isEgreso ||
      (
        !loadingSaldo &&
        saldoEfectivo !== null &&
        !excedeSaldo
      )
    );

  const cargarSaldoActual = useCallback(async ({ silent = false } = {}) => {
    // También bloquea el botón durante la revalidación previa al POST.
    // "silent" permite distinguir la recarga manual de la validación interna.
    void silent;
    setLoadingSaldo(true);
    setSaldoError('');

    try {
      const { data: estadoData } = await api.get('/pos_cajas/estado');

      if (!isCajaAbiertaResponse(estadoData)) {
        setSaldoEfectivo(null);
        setSaldoError('No existe una caja abierta.');
        return null;
      }

      let cajaActual = normalizeCajaResponse(estadoData);
      const cajaId = cajaActual?.id;

      if (cajaId) {
        try {
          const { data: detalleData } = await api.get(`/pos_cajas/${cajaId}/estado`);
          cajaActual = normalizeCajaResponse(detalleData) || cajaActual;
        } catch (detalleError) {
          console.warn(
            '[MovimientosCaja] No se pudo cargar detalle; se usará /pos_cajas/estado.',
            detalleError
          );
        }
      }

      const saldo = getSaldoTeoricoEfectivo(cajaActual);

      if (saldo === null) {
        setSaldoEfectivo(null);
        setSaldoError('No fue posible determinar el efectivo disponible de la caja.');
        return null;
      }

      setSaldoEfectivo(saldo);
      return saldo;
    } catch (err) {
      console.error('Error consultando saldo de caja:', err);

      setSaldoEfectivo(null);
      setSaldoError(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          'No fue posible verificar el efectivo disponible.'
      );

      return null;
    } finally {
      setLoadingSaldo(false);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      setSaldoEfectivo(null);
      setSaldoError('');
      setLoadingSaldo(false);
      return;
    }

    cargarSaldoActual();
  }, [open, cargarSaldoActual]);

  const reset = () => {
    setTipo('egreso');
    setMontoStr('');
    setDesc('');
  };

  const handleClose = () => {
    if (isSaving) return;

    reset();
    onClose();
  };

  const closeAfterSave = () => {
    reset();
    onClose();
  };

  const normalizeMovimientoResponse = (data, payload) => {
    const movimientoGuardado =
      data?.movimiento ||
      data?.data?.movimiento ||
      data?.data ||
      data ||
      {};

    return {
      ...payload,
      ...movimientoGuardado,

      id:
        movimientoGuardado.id ||
        movimientoGuardado.id_movimiento ||
        movimientoGuardado.movimiento_id ||
        null,

      folio:
        movimientoGuardado.folio ||
        movimientoGuardado.numero ||
        movimientoGuardado.codigo ||
        movimientoGuardado.id_movimiento ||
        movimientoGuardado.id ||
        null,

      tipo: movimientoGuardado.tipo || payload.tipo,

      monto: Number(
        movimientoGuardado.monto ||
          movimientoGuardado.amount ||
          movimientoGuardado.valor ||
          payload.monto
      ),

      descripcion:
        movimientoGuardado.descripcion ||
        movimientoGuardado.observacion ||
        movimientoGuardado.observaciones ||
        movimientoGuardado.detalle ||
        payload.descripcion,

      motivo:
        movimientoGuardado.motivo ||
        movimientoGuardado.concepto ||
        movimientoGuardado.glosa ||
        movimientoGuardado.descripcion ||
        payload.descripcion,

      created_at:
        movimientoGuardado.created_at ||
        movimientoGuardado.fecha ||
        movimientoGuardado.fecha_movimiento ||
        movimientoGuardado.createdAt ||
        new Date().toISOString(),

      caja_id:
        movimientoGuardado.caja_id ||
        movimientoGuardado.pos_caja_id ||
        movimientoGuardado.id_caja ||
        null,

      caja_nombre:
        movimientoGuardado.caja_nombre ||
        movimientoGuardado.nombre_caja ||
        movimientoGuardado.caja?.nombre ||
        null,

      usuario_id:
        movimientoGuardado.usuario_id ||
        movimientoGuardado.user_id ||
        movimientoGuardado.usuario?.id ||
        null,

      usuario_nombre:
        movimientoGuardado.usuario_nombre ||
        movimientoGuardado.cajero_nombre ||
        movimientoGuardado.user_name ||
        movimientoGuardado.usuario?.nombre ||
        movimientoGuardado.usuario?.name ||
        null,

      usuario_email:
        movimientoGuardado.usuario_email ||
        movimientoGuardado.cajero_email ||
        movimientoGuardado.user_email ||
        movimientoGuardado.usuario?.email ||
        null,
    };
  };

  const handleSave = async () => {
    if (!monto) {
      setSnack({
        open: true,
        msg: 'Debe ingresar un monto válido.',
        sev: 'warning',
      });
      return;
    }

    if (!descripcionLimpia) {
      setSnack({
        open: true,
        msg: 'Debe indicar una descripción o motivo.',
        sev: 'warning',
      });
      return;
    }

    if (isEgreso) {
      /*
       * Revalidamos inmediatamente antes del POST para no depender del saldo
       * que se cargó al abrir el modal.
       *
       * IMPORTANTE: el backend igualmente debe rechazar un egreso superior al
       * saldo dentro de la misma transacción que registra el movimiento.
       */
      const saldoActual = await cargarSaldoActual({ silent: true });

      if (saldoActual === null) {
        setSnack({
          open: true,
          msg: 'No fue posible verificar el efectivo disponible. Por seguridad no se registrará el retiro.',
          sev: 'error',
        });
        return;
      }

      if (monto > saldoActual) {
        setSaldoEfectivo(saldoActual);
        setSnack({
          open: true,
          msg: `El retiro (${new Intl.NumberFormat('es-CL', {
            style: 'currency',
            currency: 'CLP',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }).format(monto)}) supera el efectivo disponible (${new Intl.NumberFormat('es-CL', {
            style: 'currency',
            currency: 'CLP',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }).format(saldoActual)}).`,
          sev: 'error',
        });
        return;
      }
    }

    const payload = {
      tipo,
      monto,
      descripcion: descripcionLimpia,
    };

    setIsSaving(true);

    try {
      const { data } = await api.post('/pos_cajas/movimientos', payload);

      const movimientoParaVoucher = normalizeMovimientoResponse(data, payload);

      setSnack({
        open: true,
        msg: isEgreso
          ? 'Egreso registrado correctamente.'
          : 'Ingreso registrado correctamente.',
        sev: 'success',
      });

      onSaved?.(movimientoParaVoucher);
      closeAfterSave();
    } catch (err) {
      setSnack({
        open: true,
        msg:
          err.response?.data?.error ||
          err.response?.data?.message ||
          'Error al registrar movimiento de caja.',
        sev: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        fullScreen={fullScreen}
        PaperProps={{
          sx: {
            borderRadius: fullScreen ? 0 : 2,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #e0e0e0',
            bgcolor: '#fafafa',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <AccountBalanceWallet fontSize="small" color="action" />

            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                Movimiento de caja
              </Typography>

              <Typography variant="caption" color="text.secondary">
                Registrar egreso o ingreso manual
              </Typography>
            </Box>
          </Stack>

          <IconButton size="small" onClick={handleClose} disabled={isSaving}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 2, py: 2 }}>
          <Stack spacing={2}>
            <ToggleButtonGroup
              value={tipo}
              exclusive
              fullWidth
              onChange={(_, value) => value && setTipo(value)}
              sx={{
                '& .MuiToggleButton-root': {
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '0.9rem',
                },
              }}
            >
              <ToggleButton value="egreso">
                <RemoveCircleOutline sx={{ mr: 0.8 }} fontSize="small" />
                Egreso
              </ToggleButton>

              <ToggleButton value="ingreso">
                <AddCircleOutline sx={{ mr: 0.8 }} fontSize="small" />
                Ingreso
              </ToggleButton>
            </ToggleButtonGroup>

            <Box
              sx={{
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                px: 1.5,
                py: 1.2,
                bgcolor: '#fff',
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={1}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Efectivo teórico disponible
                  </Typography>

                  <Typography
                    variant="h6"
                    fontWeight={800}
                    color={saldoEfectivo === 0 ? 'text.secondary' : 'text.primary'}
                  >
                    {loadingSaldo ? 'Consultando…' : formattedSaldo}
                  </Typography>
                </Box>

                <Button
                  size="small"
                  variant="text"
                  disabled={loadingSaldo || isSaving}
                  onClick={() => cargarSaldoActual()}
                  sx={{ textTransform: 'none', fontWeight: 700 }}
                >
                  Actualizar
                </Button>
              </Stack>

              {saldoError ? (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  {saldoError}
                </Alert>
              ) : null}
            </Box>

            <Box
              sx={{
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                px: 1.5,
                py: 1.2,
                bgcolor: '#fafafa',
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {isEgreso ? 'Se descontará de caja' : 'Se sumará a caja'}
              </Typography>

              <Typography
                variant="h5"
                fontWeight={800}
                sx={{
                  mt: 0.2,
                  color: isEgreso ? 'error.main' : 'success.main',
                }}
              >
                {formattedMonto}
              </Typography>
            </Box>

            {excedeSaldo ? (
              <Alert severity="error">
                El retiro supera el efectivo teórico disponible en caja. Máximo permitido: {formattedSaldo}.
              </Alert>
            ) : null}

            <TextField
              autoFocus
              label="Monto"
              fullWidth
              value={montoStr}
              onChange={(e) => setMontoStr(fmtCLPInput(e.target.value))}
              inputProps={{
                inputMode: 'numeric',
                pattern: '[0-9\\.]+',
                style: {
                  fontSize: '1.25rem',
                  fontWeight: 700,
                  textAlign: 'right',
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AttachMoney fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 0.8 }}
              >
                Motivos frecuentes
              </Typography>

              <Stack direction="row" flexWrap="wrap" gap={0.8}>
                {QUICK_REASONS[tipo].map((reason) => (
                  <Chip
                    key={reason}
                    label={reason}
                    size="small"
                    variant={descripcion === reason ? 'filled' : 'outlined'}
                    onClick={() => setDesc(reason)}
                    sx={{
                      borderRadius: 1.5,
                      fontWeight: 600,
                    }}
                  />
                ))}
              </Stack>
            </Box>

            <TextField
              label="Descripción / motivo"
              fullWidth
              multiline
              minRows={2}
              value={descripcion}
              onChange={(e) => setDesc(e.target.value)}
              placeholder={
                isEgreso
                  ? 'Ej: Retiro por gasto menor'
                  : 'Ej: Aporte de efectivo para caja'
              }
            />
          </Stack>
        </DialogContent>

        <Divider />

        <DialogActions
          sx={{
            px: 2,
            py: 1.5,
            bgcolor: '#fafafa',
            flexDirection: fullScreen ? 'column-reverse' : 'row',
            gap: fullScreen ? 1 : 0,
          }}
        >
          <Button
            fullWidth={fullScreen}
            onClick={handleClose}
            disabled={isSaving}
            color="inherit"
            sx={{
              textTransform: 'none',
              fontWeight: 700,
            }}
          >
            Cancelar
          </Button>

          <Button
            fullWidth={fullScreen}
            variant="contained"
            onClick={handleSave}
            disabled={!canSave}
            color={isEgreso ? 'error' : 'success'}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              minWidth: 170,
            }}
          >
            {isSaving ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Guardar movimiento'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snack.sev}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{
            whiteSpace: 'pre-line',
          }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
}

ModalMovimientosCaja.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
};